<template>
  <div class="page-container">
    <div class="settings-form">
      <div class="header-section">
        <h2 class="page-title">账号管理</h2>

        <div class="license-info">
          <div class="license-item">
            <span class="label">总授权数：</span>
            <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
          </div>
          <div class="license-item">
            <span class="label">已使用：</span>
            <span class="value">{{ licenseInfo.used }}</span>
          </div>
          <div class="license-item">
            <span class="label">剩余可用：</span>
            <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining }}</span>
          </div>
        </div>
      </div>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.ghz_name" placeholder="请输入账号名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增账号
        </el-button>

        <el-button type="info" size="large" @click="batchCheckAuthorization" :loading="batchCheckLoading"
          style="margin-left: 15px">
          <el-icon>
            <Refresh />
          </el-icon>
          一键检测RPA授权
        </el-button>
      </div>


      <div class="batch-actions" v-if="selectedIds.length > 0">
        <el-button type="primary" size="large" @click="showBatchProxyDialog">
          <el-icon>
            <Connection />
          </el-icon>
          批量设置代理IP (已选 {{ selectedIds.length }} 个)
        </el-button>
        <el-button type="primary" size="large" @click="showBatchCategoryDialog">
          <el-icon>
            <Folder />
          </el-icon>
          批量设置分组 (已选 {{ selectedIds.length }} 个)
        </el-button>
        <el-button type="text" @click="clearSelection" style="margin-left: 15px;">
          <el-icon>
            <Close />
          </el-icon> 取消选择
        </el-button>
      </div>

      <!-- 账号列表 -->
      <el-table ref="accountTable" :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
        @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="ghz_name" label="账号名称" width="180" />
        <el-table-column prop="field_type" label="账号类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getAccountTypeTagType(row.field_type)">
              {{ getAccountTypeText(row.field_type) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 在表格列中增加分组列 -->
        <el-table-column label="分组" width="180">
          <template #default="{ row }">
            <el-tag v-if="row.category_name" type="info">{{ row.category_name }}</el-tag>
            <span v-else class="empty-value">未分组</span>
          </template>
        </el-table-column>


        <el-table-column label="授权方式" width="120">
          <template #default="{ row }">
            <template v-if="row.auth_method === 1 || row.auth_method === 2 || row.auth_method === 3">
              <el-tag :type="row.auth_method === 2 ? 'success' : 'warning'">
                {{ getAuthMethodText(row.auth_method) }}
              </el-tag>
            </template>
            <span v-else class="empty-value">-</span>
          </template>
        </el-table-column>

        <!-- 新增：开放平台授权列 -->
        <el-table-column label="开放平台" width="120">
          <template #default="{ row }">
            <div v-if="row.auth_method === 2">
              <el-button v-if="!row.access_token || row.access_token === 'null' || row.access_token === '[]'"
                type="primary" size="small" @click="handleOpenAuth(row)" :loading="authLoading[row.id]">
                在线授权
              </el-button>
              <el-button v-else type="success" size="small" @click="handleOpenAuth(row)" :loading="authLoading[row.id]">
                重新授权
              </el-button>
            </div>
            <span v-else class="empty-value">-</span>
          </template>
        </el-table-column>

        <el-table-column label="RPA授权" width="220">
          <template #default="{ row }">
            <div class="rpa-auth-buttons">
              <!-- 原有授权按钮 -->
              <el-button v-if="!row.cookies || row.cookies === 'null' || row.cookies === '[]'" type="primary"
                size="small" @click="startAuthorization(row)">
                点击授权
              </el-button>
              <el-button v-else type="warning" size="small" @click="startAuthorization(row)">
                重新授权
              </el-button>

              <!-- 新增检测授权按钮 -->
              <el-button :disabled="!row.cookies || row.cookies === 'null' || row.cookies === '[]'" type="success"
                size="small" @click="checkAuthorization(row)" :loading="checkLoading[row.id]">
                检测授权
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="IP+端口" width="150">
          <template #default="{ row }">
            <span v-if="row.ip_port">{{ row.ip_port }}</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>
        <el-table-column label="账号密码" width="180">
          <template #default="{ row }">
            <span v-if="row.ip_password" class="password-preview">
              {{ row.ip_password }}
            </span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchAccountList" @current-change="fetchAccountList" />
      </div>
    </div>

    <!-- 添加批量设置代理IP的对话框 -->
    <el-dialog v-model="batchProxyDialogVisible" title="批量设置代理IP" width="500px">
      <el-form ref="batchProxyForm" :model="batchProxyForm" label-width="120px">
        <el-form-item label="IP+端口">
          <el-input v-model="batchProxyForm.ip_port" placeholder="格式：232.33.1.1:16817" />
        </el-form-item>

        <el-form-item label="账号密码">
          <el-input v-model="batchProxyForm.ip_password" placeholder="格式：账号:密码，如：34123:55332" show-password />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="batchProxyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchProxy" :loading="batchProxyLoading">确认设置</el-button>
      </template>
    </el-dialog>

    <!-- 新增：API配额对话框 -->
    <el-dialog v-model="apiQuotaDialogVisible" title="API调用次数" width="500px">
      <div v-if="quotaLoading" class="loading-container">
        <el-icon class="is-loading" size="30">
          <Loading />
        </el-icon>
        <span>正在查询API使用情况...</span>
      </div>

      <div v-else class="quota-container">
        <div class="quota-section" v-if="materialQuota">
          <h3>素材接口使用情况</h3>
          <div class="quota-item">
            <span class="label">API路径：</span>
            <span class="value">/cgi-bin/material/add_material</span>
          </div>
          <div class="quota-item">
            <span class="label">每日限额：</span>
            <span class="value">{{ materialQuota.daily_limit }}</span>
          </div>
          <div class="quota-item">
            <span class="label">已使用：</span>
            <span class="value">{{ materialQuota.used }}</span>
          </div>
          <div class="quota-item">
            <span class="label">剩余次数：</span>
            <span class="value">{{ materialQuota.remain }}</span>
          </div>
        </div>

        <div class="quota-section" v-if="draftQuota">
          <h3>草稿接口使用情况</h3>
          <div class="quota-item">
            <span class="label">API路径：</span>
            <span class="value">/cgi-bin/draft/add</span>
          </div>
          <div class="quota-item">
            <span class="label">每日限额：</span>
            <span class="value">{{ draftQuota.daily_limit }}</span>
          </div>
          <div class="quota-item">
            <span class="label">已使用：</span>
            <span class="value">{{ draftQuota.used }}</span>
          </div>
          <div class="quota-item">
            <span class="label">剩余次数：</span>
            <span class="value">{{ draftQuota.remain }}</span>
          </div>
        </div>

        <div v-if="quotaError" class="quota-error">
          <el-alert :title="quotaError" type="error" :closable="false" />
        </div>
      </div>

      <template #footer>
        <el-button @click="apiQuotaDialogVisible = false">关闭</el-button>
        <el-button type="danger" @click="resetApiQuota" :loading="resetLoading">重置调用次数</el-button>

      </template>
    </el-dialog>

    <!-- 新增批量设置分组对话框 -->
    <el-dialog v-model="batchCategoryDialogVisible" title="批量设置分组" width="500px">
      <el-form ref="batchCategoryForm" :model="batchCategoryForm" label-width="120px">
        <el-form-item label="选择分组">
          <el-select v-model="batchCategoryForm.category_id" placeholder="请选择分组" clearable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="batchCategoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchCategory" :loading="batchCategoryLoading">
          确认设置
        </el-button>
      </template>
    </el-dialog>
    <!-- 编辑AppID对话框 -->
    <el-dialog v-model="editAppIdDialogVisible" title="编辑AppID" width="500px">
      <el-form ref="editAppIdFormRef" :model="editAppIdForm" :rules="editAppIdRules" label-width="120px">
        <el-form-item label="AppID" prop="ghz_appid">
          <el-input v-model="editAppIdForm.ghz_appid" placeholder="请输入开放平台AppID" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editAppIdDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditAppId" :loading="editAppIdLoading">
          确认
        </el-button>
      </template>
    </el-dialog>
    <!-- QR Code Dialog -->
    <el-dialog v-model="qrDialogVisible" title="账号授权" width="400px">
      <div class="qr-code-container">
        <div v-if="qrLoading" class="loading-container">
          <el-icon class="is-loading" size="30">
            <Loading />
          </el-icon>
          <span>正在获取二维码...</span>
        </div>

        <!-- 二维码显示区域 -->
        <template v-if="!qrLoading">
          <img v-if="qrCodeBase64" :src="qrCodeBase64" alt="账号授权二维码" class="qr-code-image" />

          <!-- 验证码输入区域 -->
          <div v-if="showVerificationCode" class="verification-code-section">
            <el-alert title="需要短信验证码" type="warning" :closable="false" style="margin-bottom: 15px;" />
            <el-input v-model="verificationCode" :placeholder="'请输入验证码 (' + verificationElement + ')'" clearable
              @keyup.enter="confirmAuthorization" />
          </div>
        </template>

        <div class="qr-instructions">
          <p v-if="!showVerificationCode" class="auth-instruction"
            :class="getPlatformClass(currentAuthRow?.field_type)">
            请使用 <strong>{{ getPlatformName(currentAuthRow?.field_type) }}</strong> APP 扫描上方二维码授权账号
          </p>
          <p v-else>请输入收到的短信验证码</p>
          <p>完成后点击下方确认按钮</p>
        </div>
      </div>

      <template #footer>
        <el-button @click="qrDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAuthorization" :loading="confirmLoading"
          :disabled="!qrCodeBase64 || (showVerificationCode && !verificationCode)">
          {{ showVerificationCode ? '提交验证码' : '确认授权' }}
        </el-button>
      </template>
    </el-dialog>
    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="accountForm" :model="accountForm" label-width="120px" :rules="formRules">
        <el-form-item label="账号名称" prop="ghz_name">
          <el-input v-model="accountForm.ghz_name" placeholder="请输入账号名称" size="large" />
        </el-form-item>
        <el-form-item label="账号分组">
          <el-select v-model="accountForm.category_id" placeholder="请选择分组" clearable filterable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="账号类型" prop="field_type">
          <el-select v-model="accountForm.field_type" placeholder="请选择账号类型" size="large"
            @change="handleAccountTypeChange">
            <el-option v-if="applicationMarket.wyjuzhen_dy" :label="'某音授权'" :value="2" />
            <el-option v-if="applicationMarket.wyjuzhen_ks" :label="'某手授权'" :value="3" />
            <el-option v-if="applicationMarket.wyjuzhen_sph" :label="'视频号授权'" :value="4" />
            <el-option v-if="applicationMarket.wyjuzhen_xhs" :label="'小红薯授权'" :value="5" />
            <el-option v-if="applicationMarket.wyjuzhen_bilibili" :label="'B站授权'" :value="6" />
            <el-option v-if="applicationMarket.wyjuzhen_tiktok" :label="'TK授权'" :value="7" />

          </el-select>
        </el-form-item>

        <!-- 新增：某音密码输入框 -->
        <el-form-item v-if="accountForm.field_type === 2" label="某音密码" prop="dy_password">
          <el-input v-model="accountForm.dy_password" placeholder="请输入某音密码" size="large" show-password />
          <div class="form-tip">密码必须正确，否则可能导致发送失败！</div>
        </el-form-item>

        <!-- 仅当选择公众号时显示授权方式 -->
        <el-form-item v-if="accountForm.field_type === 1" label="授权方式" prop="auth_method">
          <el-select v-model="accountForm.auth_method" placeholder="请选择授权方式" size="large">
            <el-option :label="'密钥授权'" :value="1" />
            <el-option :label="'开放平台'" :value="2" />
            <el-option :label="'RPA授权'" :value="2" />
          </el-select>
        </el-form-item>

        <!-- 仅当选择公众号且授权方式为密钥授权时显示AppID和Secret -->
        <template v-if="accountForm.field_type === 1 && accountForm.auth_method === 1">
          <el-form-item label="AppID" prop="ghz_appid">
            <el-input v-model="accountForm.ghz_appid" placeholder="请输入账号AppID" size="large" />
          </el-form-item>

          <el-form-item label="Secret" prop="ghz_secret">
            <el-input v-model="accountForm.ghz_secret" placeholder="请输入账号Secret" size="large" show-password />
          </el-form-item>
        </template>

        <el-form-item label="IP+端口">
          <el-input v-model="accountForm.ip_port" placeholder="格式：232.33.1.1:16817（可选）" size="large" />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>

        <el-form-item label="账号密码">
          <el-input v-model="accountForm.ip_password" placeholder="格式：账号:密码，如：34123:55332（可选）" size="large"
            show-password />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAccountForm">确认</el-button>
      </template>
    </el-dialog>
  </div>

</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Loading, Refresh, Folder
} from '@element-plus/icons-vue'

export default {
  name: 'AccountManagements',
  components: { Search, Plus, Edit, Delete, Loading, Refresh, Folder },
  data() {
    // IP端口格式验证（非必填）
    const validateIpPort = (rule, value, callback) => {
      if (value && !/^(\d{1,3}\.){3}\d{1,3}:\d{1,5}$/.test(value)) {
        callback(new Error('格式错误，示例：192.168.1.1:8080'));
      } else {
        callback();
      }
    };

    // 账号密码格式验证（非必填）
    const validateAccountPassword = (rule, value, callback) => {
      if (value && !/^[^:]+:[^:]+$/.test(value)) {
        callback(new Error('格式错误，示例：username:password'));
      } else {
        callback();
      }
    };

    return {
      loading: false,
      // 新增加载状态
      checkLoading: {}, // 单个检测加载状态
      batchCheckLoading: false, // 批量检测加载状态
      dialogVisible: false,
      dialogTitle: '新增账号',
      isEditMode: false,
      qrDialogVisible: false,
      qrLoading: false,
      confirmLoading: false,
      qrCodeBase64: '',
      currentAuthRow: null,
      apiQuotaDialogVisible: false,
      quotaLoading: false,
      materialQuota: null,
      draftQuota: null,
      quotaError: '',
      currentQuotaRow: null,
      webId: '',
      resetLoading: false,
      authLoading: {}, // 记录每个账号的授权加载状态
      editAppIdDialogVisible: false,
      selectedIds: [], // 存储选中的账号ID
      batchProxyDialogVisible: false,
      batchProxyLoading: false,
      showVerificationCode: false, // 是否显示验证码输入框
      verificationCode: '',        // 用户输入的验证码
      verificationElement: '',     // 验证码输入框的提示信息
      batchProxyForm: {
        ip_port: '',
        ip_password: ''
      },
      applicationMarket: {
        ksjz_qt: false,
        ksjz_app: false
      },

      batchCategoryDialogVisible: false,
      batchCategoryLoading: false,
      batchCategoryForm: {
        category_id: null
      },
      categoryOptions: [], // 存储分组选项

      editAppIdForm: {
        id: null,
        store_id: null,
        ghz_appid: ''
      },
      editAppIdRules: {
        ghz_appid: [
          { required: true, message: '请输入AppID', trigger: 'blur' }
        ]
      },
      searchForm: {
        ghz_name: ''
      },
      accountForm: {
        id: null,
        ghz_name: '',
        field_type: 2, // 默认公众号
        dy_password: '', // 新增：某音密码字段
        ip_port: '',
        ip_password: '',
        auth_method: 1, // 默认密钥授权
        ghz_appid: '',
        ghz_secret: '',
        notes: ''
      },
      formRules: {
        ghz_name: [
          { required: true, message: '请输入账号名称', trigger: 'blur' }
        ],
        field_type: [
          { required: true, message: '请选择账号类型', trigger: 'change' }
        ],
        auth_method: [
          {
            required: () => this.accountForm.field_type === 1,
            message: '请选择授权方式',
            trigger: 'change'
          }
        ],
        ghz_appid: [
          {
            required: () => this.accountForm.field_type === 1 && this.accountForm.auth_method === 1,
            message: '请输入账号AppID',
            trigger: 'blur'
          }
        ],
        ghz_secret: [
          {
            required: () => this.accountForm.field_type === 1 && this.accountForm.auth_method === 1,
            message: '请输入账号Secret',
            trigger: 'blur'
          }
        ],
        ip_port: [
          { validator: validateIpPort, trigger: 'blur' }
        ],
        ip_password: [
          { validator: validateAccountPassword, trigger: 'blur' }
        ],
        // 新增某音密码验证规则
        dy_password: [
          {
            required: () => this.accountForm.field_type === 2,
            message: '请输入某音密码',
            trigger: 'blur'
          }
        ]
      },
      accountList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      // 新增授权信息
      licenseInfo: {
        total: 0,
        used: 0,
        remaining: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchAccountList();
    this.fetchLicenseInfo(); // 新增：获取授权信息
    this.fetchApplicationMarket(); // 新增这行
    this.fetchCategories(); // 新增：加载分组选项


  },
  methods: {

    // 获取平台名称
    getPlatformName(type) {
      const platformNames = {
        1: '微信',
        2: '抖音',
        3: '快手',
        4: '视频号',
        5: '小红书',
        6: 'B站',
        7: 'TikTok'
      };
      return platformNames[type] || '对应平台';
    },

    // 获取平台对应的CSS类名
    getPlatformClass(type) {
      const platformClasses = {
        1: 'wechat',
        2: 'douyin',
        3: 'kuaishou',
        4: 'video-channel',
        5: 'xiaohongshu',
        6: 'bilibili',
        7: 'tiktok'
      };
      return platformClasses[type] || 'wechat';
    },


    getAccountTypeText(type) {
      const types = {
        1: '公众号',
        2: '某音',
        3: '某手',
        4: '视频号',
        5: '小红薯',
        6: 'B站',
        7: 'Tiktok',
      };
      return types[type] || '未知类型';
    },

    // 获取账号类型标签样式
    getAccountTypeTagType(type) {
      const types = {
        1: 'success', // 公众号 - 绿色
        2: 'danger',  // 某音 - 红色
        3: 'warning', // 某手 - 橙色
        4: 'primary', // 视频号 - 蓝色
        5: 'danger',  // 小红薯 - 红色
        6: 'info',    // B站 - 青色
        7: 'info'     // Tiktok - 青色
      };
      return types[type] || 'default'; // 默认样式
    },

    // 新增方法：获取分组选项
    async fetchCategories() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/account_category_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.categoryOptions = data.data;
        }
      } catch (error) {
        console.error('获取分组列表失败:', error);
      }
    },

    // 显示批量设置分组对话框
    showBatchCategoryDialog() {
      this.batchCategoryForm = {
        category_id: null
      };
      this.batchCategoryDialogVisible = true;
    },

    // 提交批量设置分组
    async submitBatchCategory() {
      if (this.selectedIds.length === 0) {
        this.$message.warning('请先选择要设置的账号');
        return;
      }

      try {
        this.batchCategoryLoading = true;

        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'batch_set_category',
            ids: this.selectedIds,
            category_id: this.batchCategoryForm.category_id,
            store_id: this.userInfo.id
          }
        );

        if (response.data.success) {
          this.$message.success(`成功为 ${this.selectedIds.length} 个账号设置分组`);
          this.batchCategoryDialogVisible = false;
          this.clearSelection();
          this.fetchAccountList();
        } else {
          this.$message.error(response.data.error || '批量设置分组失败');
        }
      } catch (error) {
        let errorMsg = '批量设置分组失败';
        if (error.response?.data?.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('批量设置分组错误:', error);
      } finally {
        this.batchCategoryLoading = false;
      }
    },
    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id);
    },

    // 清空选择
    // 修改 clearSelection 方法
    clearSelection() {
      if (this.$refs.accountTable) {
        this.$refs.accountTable.clearSelection();
      }
      this.selectedIds = [];
    },

    // 显示批量设置对话框
    showBatchProxyDialog() {
      this.batchProxyForm = {
        ip_port: '',
        ip_password: ''
      };
      this.batchProxyDialogVisible = true;
    },

    // 提交批量设置
    async submitBatchProxy() {
      if (this.selectedIds.length === 0) {
        this.$message.warning('请先选择要设置的账号');
        return;
      }

      try {
        this.batchProxyLoading = true;

        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'batch_set_proxy',
            ids: this.selectedIds,
            ip_port: this.batchProxyForm.ip_port,
            ip_password: this.batchProxyForm.ip_password,
            store_id: this.userInfo.id
          }
        );

        if (response.data.success) {
          this.$message.success(`成功为 ${this.selectedIds.length} 个账号设置代理`);
          this.batchProxyDialogVisible = false;
          this.clearSelection();
          this.fetchAccountList();
        } else {
          this.$message.error(response.data.error || '批量设置代理失败');
        }
      } catch (error) {
        let errorMsg = '批量设置代理失败';
        if (error.response?.data?.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('批量设置代理错误:', error);
      } finally {
        this.batchProxyLoading = false;
      }
    },
    async fetchApplicationMarket() {
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/shop_api.php`,
          {
            action: 'query',
            account: this.userInfo.account
          }
        );

        if (response.data.data?.application_market) {
          this.applicationMarket = this.parseApplicationMarket(response.data.data.application_market);
        }
      } catch (error) {
        console.error('获取应用市场数据失败:', error);
      }
    },

    parseApplicationMarket(marketStr) {
      try {
        return JSON.parse(marketStr);
      } catch (e) {
        console.error('解析 application_market 失败:', e);
        return {
          ksjz_qt: false,
          ksjz_app: false
        };
      }
    },

    handleAccountTypeChange(type) {
      // 非公众号类型时，设置auth_method为3（RPA授权）
      if (type !== 1) {
        this.accountForm.auth_method = 3;
      } else {
        // 公众号类型时，重置为密钥授权
        this.accountForm.auth_method = 1;
      }

      // 如果不是某音类型，清除某音密码
      if (type !== 2) {
        this.accountForm.dy_password = '';
      }

      // 重置相关字段的验证状态
      if (this.$refs.accountForm) {
        this.$refs.accountForm.clearValidate(['auth_method', 'ghz_appid', 'ghz_secret']);
      }

      // 根据类型重置相关字段
      if (type === 1) {
        // 公众号
        this.accountForm.auth_method = 1;
      } else {
        // 其他平台
        this.accountForm.auth_method = 3;
        this.accountForm.ghz_appid = '';
        this.accountForm.ghz_secret = '';
      }
    },


    // 打开编辑AppID对话框
    openEditAppIdDialog(row) {
      this.editAppIdForm = {
        id: row.id,
        store_id: this.userInfo.id,
        ghz_appid: row.ghz_appid || ''
      };
      this.editAppIdDialogVisible = true;
    },

    // 提交AppID更新
    async submitEditAppId() {
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'update_appid',
            ...this.editAppIdForm
          }
        );

        if (response.data.error) {
          this.$message.error(response.data.error || '更新失败');
        } else if (response.data.success) {
          this.$message.success('AppID更新成功');
          this.editAppIdDialogVisible = false;
          this.fetchAccountList(); // 刷新列表
        }
      } catch (error) {
        let errorMsg = '更新失败';
        if (error.response && error.response.data && error.response.data.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('更新AppID错误:', error);
      }
    },
    // 获取授权方式文本
    getAuthMethodText(method) {
      const methods = {
        1: '密钥授权',
        2: '开放平台',
        3: 'RPA授权'
      };
      return methods[method] || '-';
    },
    // 修改 checkAuthorization 方法
    async checkAuthorization(row) {
      // 使用直接赋值方式替代 this.$set
      this.checkLoading = { ...this.checkLoading, [row.id]: true };

      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/update_cookies_by_ids.php`,
          {
            ids: [row.id],
            field_type: row.field_type // 传递账号类型

          }
        );

        if (response.data.success) {
          const result = response.data.results.find(r => r.id === row.id);
          if (result) {
            if (result.status === 'success') {
              this.$message.success(`ID ${row.id} 授权有效，已更新cookies`);
            } else if (result.status === 'cooldown') {
              this.$message.warning(`ID ${row.id} 处于冷却期，${result.next_available}后可更新`);
            } else {
              this.$message.warning(`ID ${row.id} ${result.message}`);
            }
          }
        } else {
          this.$message.error(response.data.error || '检测授权失败');
        }
      } catch (error) {
        let errorMsg = '检测授权失败';
        if (error.response && error.response.data && error.response.data.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('检测授权错误:', error);
      } finally {
        // 使用直接赋值方式替代 this.$set
        this.checkLoading = { ...this.checkLoading, [row.id]: false };
        this.fetchAccountList(); // 刷新列表
      }
    },

    // 新增：批量检测授权
    async batchCheckAuthorization() {
      // 过滤出有cookies的记录
      const validIds = this.accountList
        .filter(item => item.cookies && item.cookies !== 'null' && item.cookies !== '[]')
        .map(item => ({
          id: item.id,
          field_type: item.field_type // 传递账号类型
        }));

      if (validIds.length === 0) {
        this.$message.warning('没有可检测的授权账号');
        return;
      }

      this.$confirm(`确定要检测${validIds.length}个账号的授权状态吗？`, '批量检测', {
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(async () => {
        this.batchCheckLoading = true;

        try {
          const response = await axios.post(
            `${config.BASE_URL}/shop/api/update_cookies_by_ids.php`,
            {
              ids: validIds
            }
          );

          if (response.data.success) {
            // 统计结果
            const stats = {
              success: 0,
              cooldown: 0,
              error: 0
            };

            response.data.results.forEach(result => {
              if (result.status === 'success') stats.success++;
              else if (result.status === 'cooldown') stats.cooldown++;
              else stats.error++;
            });

            this.$notify({
              title: '批量检测完成',
              message: `成功: ${stats.success}个, 冷却中: ${stats.cooldown}个, 失败: ${stats.error}个`,
              type: 'success',
              duration: 5000
            });
          } else {
            this.$message.error(response.data.error || '批量检测失败');
          }
        } catch (error) {
          let errorMsg = '批量检测失败';
          if (error.response && error.response.data && error.response.data.error) {
            errorMsg = error.response.data.error;
          }
          this.$message.error(errorMsg);
          console.error('批量检测错误:', error);
        } finally {
          this.batchCheckLoading = false;
          this.fetchAccountList(); // 刷新列表
        }
      }).catch(() => {
        // 用户取消
      });
    },

    // 处理开放平台授权
    handleOpenAuth(row) {
      // 显示加载状态
      this.authLoading[row.id] = true;


      // 构建授权URL
      const authUrl = `${config.BASE_URL}/weixin/get_auth_url.php?id=${row.id}&store_id=${this.userInfo.id}`;

      // 在新标签页打开授权页面
      const authWindow = window.open(authUrl, '_blank');

      // 定时检查授权状态
      this.checkAuthStatus(row.id, authWindow);
    },

    // 检查授权状态
    async checkAuthStatus(id) {
      let interval;

      try {
        // 每2秒检查一次授权状态
        interval = setInterval(async () => {
          try {
            // 检查授权是否成功
            const response = await axios.get(
              `${config.BASE_URL}/weixin/check_auth_status.php?id=${id}`
            );

            // 处理服务器响应
            if (response.data.success === true) {
              // 授权成功 - 停止轮询
              clearInterval(interval);
              this.authLoading = { ...this.authLoading, [id]: false };


              // 提示用户
              this.$notify({
                title: '授权成功',
                message: `账号 ${response.data.appid} 授权已完成`,
                type: 'success',
                duration: 3000
              });

              // 刷新数据
              this.fetchAccountList();
            } else {
              // 授权未完成 - 继续轮询（不会清除定时器）
              // 可以在这里添加调试日志
              console.log('授权尚未完成，继续轮询...');
            }
          } catch (error) {
            // 请求失败的处理
            console.error('检查授权状态请求失败:', error);
          }
        }, 2000);

        // 设置10分钟超时（避免无限轮询）
        setTimeout(() => {
          if (interval) {
            clearInterval(interval);
            this.authLoading = { ...this.authLoading, [id]: false };
            this.$notify.error({
              title: '授权超时',
              message: '授权流程未在10分钟内完成',
              duration: 7000
            });
          }
        }, 10 * 60 * 1000); // 10分钟
      } catch (error) {
        this.authLoading = { ...this.authLoading, [id]: false };
        this.$notify.error({
          title: '授权错误',
          message: '授权请求发送失败: ' + error.message,
          duration: 5000
        });
      }
    },
    // 新增：重置API调用次数
    async resetApiQuota() {
      this.$confirm('确定要重置API调用次数吗？每月只有10次机会，请谨慎操作！', '警告', {
        confirmButtonText: '确定重置',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.resetLoading = true;
        try {
          // 获取最新access_token
          const tokenResponse = await axios.get(
            `${config.BASE_URL}/shop/api/get_access_token.php?id=${this.currentQuotaRow.id}`
          );

          if (tokenResponse.data.status !== 'success') {
            this.$message.error(tokenResponse.data.error || '获取账号信息失败');
            return;
          }

          const { access_token, appid } = tokenResponse.data;

          // 调用重置接口
          const response = await axios.post(
            `${config.BASE_URL}/shop/api/reset_api_quota.php`,
            {
              access_token,
              appid
            }
          );

          if (response.data.errcode === 0) {
            this.$message.success('重置成功');
            // 重置后重新获取配额信息
            this.showApiQuota(this.currentQuotaRow);
          } else {
            let errorMsg = response.data.errmsg || '重置失败';

            // 处理特定错误码
            if (response.data.errcode === 48006) {
              errorMsg = '本月重置次数已达上限（10次）';
            } else if (response.data.errcode === 40013) {
              errorMsg = '无效的APPID';
            }

            this.$message.error(`重置失败: ${errorMsg}`);
          }
        } catch (error) {
          let errorMsg = '重置失败';
          if (error.response && error.response.data && error.response.data.errmsg) {
            errorMsg = error.response.data.errmsg;
          }
          this.$message.error(`重置失败: ${errorMsg}`);
        } finally {
          this.resetLoading = false;
        }
      }).catch(() => {
        // 用户取消操作
        this.resetLoading = false;
      });
    },
    // 显示API配额弹窗
    async showApiQuota(row) {
      this.apiQuotaDialogVisible = true;
      this.quotaLoading = true;
      this.currentQuotaRow = row;
      this.materialQuota = null;
      this.draftQuota = null;
      this.quotaError = '';

      try {
        // 获取token
        const response = await axios.get(
          `${config.BASE_URL}/shop/api/get_access_token.php?id=${row.id}`
        );

        if (response.data.status === 'success') {
          const accessToken = response.data.access_token;
          const appid = response.data.appid;


          // 保存token和appid用于后续操作
          this.currentQuotaRow.access_token = accessToken;
          this.currentQuotaRow.appid = appid;

          // 查询素材接口配额
          const materialResponse = await axios.post(
            `${config.BASE_URL}/shop/api/get_api_quota.php`,
            {
              access_token: accessToken,
              cgi_path: '/cgi-bin/material/add_material'
            }
          );

          if (materialResponse.data.quota) {
            this.materialQuota = materialResponse.data.quota;
          }

          // 查询草稿接口配额
          const draftResponse = await axios.post(
            `${config.BASE_URL}/shop/api/get_api_quota.php`,
            {
              access_token: accessToken,
              cgi_path: '/cgi-bin/draft/add'
            }
          );

          if (draftResponse.data.quota) {
            this.draftQuota = draftResponse.data.quota;
          }

          if (!this.materialQuota && !this.draftQuota) {
            this.quotaError = '未能获取API使用信息';
          }
        } else {
          this.quotaError = response.data.error || '获取账号Token失败';
        }
      } catch (error) {
        console.error('获取API配额失败:', error);
        this.quotaError = '获取API使用数据失败，请稍后再试';
      } finally {
        this.quotaLoading = false;
      }
    },

    // 开始授权流程
    async startAuthorization(row) {
      this.currentAuthRow = row;
      this.qrDialogVisible = true;
      this.qrLoading = true;
      this.qrCodeBase64 = '';

      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_cookies.php`,
          {
            action: 'get_qr',
            id: this.currentAuthRow.id,
            store_id: this.userInfo.id,
            field_type: row.field_type // 传递账号类型

          }
        );

        if (response.data.status === 'success') {
          this.qrCodeBase64 = response.data.base64;
          this.webId = response.data.web_id;
          this.fingerprint = response.data.fingerprint;
        } else {
          this.$message.error(response.data.error || '获取二维码失败');
        }
      } catch (error) {
        let errorMsg = '获取二维码失败';
        if (error.response && error.response.data && error.response.data.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('获取二维码错误:', error);
      } finally {
        this.qrLoading = false;
      }
    },

    // 确认授权
    async confirmAuthorization() {
      try {
        this.confirmLoading = true;

        const payload = {
          action: 'update_cookies',
          web_id: this.webId,
          fingerprint: this.fingerprint,
          id: this.currentAuthRow.id,
          store_id: this.userInfo.id,
          field_type: this.currentAuthRow.field_type
        };

        // 如果需要验证码，添加验证码参数
        if (this.showVerificationCode) {
          payload.verification_code = this.verificationCode;
        }

        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_cookies.php`,
          payload
        );

        if (response.data.status === 'success') {
          this.$message.success('授权成功');
          this.qrDialogVisible = false;
          this.fetchAccountList();
        }
        // 处理需要验证码的情况
        else if (response.data.status === 'verification_required') {
          this.showVerificationCode = true;
          this.verificationElement = response.data.verification_element || '#button-input';
          this.$message.warning('需要短信验证码才能继续');
        }
        else {
          this.$message.error(response.data.message || '授权失败');
        }
      } catch (error) {
        let errorMsg = '授权失败';
        if (error.response?.data?.error) {
          errorMsg = error.response.data.error;
        }
        this.$message.error(errorMsg);
        console.error('授权错误:', error);
      } finally {
        this.confirmLoading = false;
      }
    },


    // 处理授权方式变更
    handleAuthMethodChange(value) {
      // 当切换为非密钥授权时，清空AppID和Secret
      if (value !== 1) {
        this.accountForm.ghz_appid = '';
        this.accountForm.ghz_secret = '';
      }

      // 重置相关字段的验证状态
      if (this.$refs.accountForm) {
        this.$refs.accountForm.clearValidate(['ghz_appid', 'ghz_secret']);
      }
    },

    // 获取账号列表
    async fetchAccountList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.ghz_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.ghz_name) {
          params.ghz_name = this.searchForm.ghz_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          params
        );

        if (data.data) {
          this.accountList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取账号列表失败');
        console.error('获取账号列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchAccountList();
    },

    // 新增：获取授权信息
    async fetchLicenseInfo() {
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'license_info',
            store_id: this.userInfo.id
          }
        );

        if (response.data && !response.data.error) {
          this.licenseInfo = {
            total: response.data.total || 0,
            used: response.data.used || 0,
            remaining: response.data.remaining || 0
          };
        }
      } catch (error) {
        console.error('获取授权信息失败:', error);
      }
    },

    // 打开新增对话框
    openAddDialog() {
      // 检查授权数量
      if (this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) {
        this.$message.warning('授权账号数已满，请联系管理员充值！');
        return;
      }

      this.dialogTitle = '新增账号';
      this.isEditMode = false;

      // 根据应用市场授权情况设置默认类型
      let defaultType = 2; // 默认值

      // 检查哪些类型已授权，按优先级选择第一个可用的
      if (this.applicationMarket.wyjuzhen_dy) {
        defaultType = 2; // 某音
      } else if (this.applicationMarket.wyjuzhen_ks) {
        defaultType = 3; // 某手
      } else if (this.applicationMarket.wyjuzhen_sph) {
        defaultType = 4; // 视频号
      } else if (this.applicationMarket.wyjuzhen_xhs) {
        defaultType = 5; // 小红薯
      } else if (this.applicationMarket.wyjuzhen_bilibili) {
        defaultType = 6; // B站
      } else if (this.applicationMarket.wyjuzhen_tiktok) {
        defaultType = 7; // TK
      }

      this.accountForm = {
        id: null,
        category_id: null,
        ghz_name: '',
        field_type: defaultType, // 使用动态设置的默认类型
        dy_password: '', // 新增：初始化某音密码字段
        ip_port: '',
        ip_password: '',
        auth_method: defaultType === 1 ? 1 : 3, // 如果是公众号则用密钥授权，否则用RPA授权
        ghz_appid: '',
        ghz_secret: '',
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑账号';
      this.isEditMode = true;
      this.accountForm = {
        id: row.id,
        category_id: row.category_id,
        ghz_name: row.ghz_name,
        field_type: row.field_type,
        dy_password: row.dy_password || '', // 新增：编辑时填充某音密码
        ip_port: row.ip_port,
        ip_password: row.ip_password,
        auth_method: row.auth_method,
        ghz_appid: row.ghz_appid,
        ghz_secret: row.ghz_secret,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）- 包含详细的错误处理
    submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (valid) {

          // 非公众号类型时，设置auth_method为3
          if (this.accountForm.field_type !== 1) {
            this.accountForm.auth_method = 3;
          }

          // 如果不是某音类型，清除某音密码
          if (this.accountForm.field_type !== 2) {
            this.accountForm.dy_password = '';
          }

          try {
            // 如果是新增操作，检查授权数量
            if (!this.isEditMode) {
              const canAddResponse = await axios.post(
                `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
                {
                  action: 'can_add',
                  store_id: this.userInfo.id
                }
              );


              if (canAddResponse.data.error) {
                if (canAddResponse.data.code === 'QUOTA_EXCEEDED') {
                  this.$message.warning('授权账号数已满，请联系管理员充值！');
                  return;
                }
                this.$message.error(canAddResponse.data.error || '操作失败');
                return;
              }

              if (!canAddResponse.data.can_add) {
                this.$message.warning('授权账号数已满，请联系管理员充值！');
                return;
              }
            }

            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.accountForm,
              store_id: this.userInfo.id
            };

            // 如果授权方式不是密钥授权，清除AppID和Secret
            if (payload.auth_method !== 1) {
              payload.ghz_appid = '';
              payload.ghz_secret = '';
            }

            const response = await axios.post(
              `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
              {
                action,
                ...payload
              }
            );

            // 处理API响应数据
            if (response.data.error) {
              if (response.data.reason && response.data.code) {
                this.$message.error(`${response.data.error}: ${response.data.reason} (错误代码: ${response.data.code})`);
              } else {
                this.$message.error(response.data.error || '操作失败');
              }
            } else if (response.data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchAccountList();
            } else {
              this.$message.error('操作失败，未知错误');
            }
          } catch (error) {
            // 处理网络和API错误
            let errorMsg = '操作失败';

            // 检查是否包含后端返回的错误详情
            if (error.response && error.response.data) {
              const errorData = error.response.data;

              if (errorData.error && errorData.reason && errorData.code) {
                errorMsg = `${errorData.error}: ${errorData.reason} (错误代码: ${errorData.code})`;
              } else if (errorData.error) {
                errorMsg = errorData.error;
              } else if (errorData.message) {
                errorMsg = errorData.message;
              }
            }
            // 网络错误的特殊情况
            else if (error.message) {
              errorMsg = error.message;
            }

            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除账号 - 包含详细的错误处理
    async handleDelete(id) {
      this.$confirm('确定要删除该账号吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await axios.post(
            `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          // 处理API响应数据
          if (response.data.error) {
            if (response.data.reason && response.data.code) {
              this.$message.error(`${response.data.error}: ${response.data.reason} (错误代码: ${response.data.code})`);
            } else {
              this.$message.error(response.data.error || '删除失败');
            }
          } else if (response.data.success) {
            this.$message.success('删除成功');
            this.fetchAccountList();
          } else {
            this.$message.error('删除失败，未知错误');
          }
        } catch (error) {
          // 处理网络和API错误
          let errorMsg = '删除失败';

          // 检查是否包含后端返回的错误详情
          if (error.response && error.response.data) {
            const errorData = error.response.data;

            if (errorData.error && errorData.reason && errorData.code) {
              errorMsg = `${errorData.error}: ${errorData.reason} (错误代码: ${errorData.code})`;
            } else if (errorData.error) {
              errorMsg = errorData.error;
            } else if (errorData.message) {
              errorMsg = errorData.message;
            }
          }
          // 网络错误的特殊情况
          else if (error.message) {
            errorMsg = error.message;
          }

          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }

  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

/* 新增头部区域样式 */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.license-info {
  display: flex;
  gap: 20px;
  background: #f0f7ff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
}

.license-item {
  display: flex;
  align-items: center;

  .label {
    color: #606266;
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #409eff;
  }
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 密码预览样式 */
.password-preview {
  font-family: monospace;
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

/* 表单提示样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

/* 空值样式 */
.empty-value {
  color: #909399;
  font-style: italic;
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;

    span {
      margin-top: 10px;
    }
  }

  .qr-code-image {
    width: 200px;
    height: 200px;
    margin-bottom: 20px;
    border: 1px solid #eee;
  }

  .qr-instructions {
    margin-top: 15px;
    text-align: center;
    color: #606266;
    font-size: 14px;

    p {
      margin: 5px 0;
    }
  }
}


/* 新增：API配额弹窗样式 */
.quota-container {
  padding: 15px;
}

.quota-section {
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #eee;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  h3 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #409eff;
    font-size: 16px;
  }
}

.quota-item {
  display: flex;
  margin-bottom: 10px;

  .label {
    width: 90px;
    font-weight: 500;
    color: #606266;
  }

  .value {
    flex: 1;
    color: #303133;
  }
}

.quota-error {
  margin-top: 15px;
}

/* 新增RPA授权按钮组样式 */
.rpa-auth-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.appid-container {
  display: flex;
  align-items: center;
}

.empty-value {
  color: #909399;
  font-style: italic;
}

.account-type-tag {
  margin-right: 5px;

  &.wechat {
    background-color: #07C160;
    color: white;
  }

  &.baijia {
    background-color: #FF9500;
    color: white;
  }

  &.xiaohongshu {
    background-color: #FF2442;
    color: white;
  }

  &.toutiao {
    background-color: #2E6FF2;
    color: white;
  }

  &.zhihu {
    background-color: #0084FF;
    color: white;
  }
}

.batch-guide {
  margin-bottom: 15px;

  .el-alert {
    padding: 12px 16px;

    p {
      margin: 5px 0;
      line-height: 1.5;
    }
  }
}

.batch-actions {
  margin-bottom: 15px;
  padding: 12px 15px;
  background-color: #f0f7ff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  border-left: 4px solid #409eff;

  .el-button {
    font-weight: 500;
  }
}

.empty-value {
  color: #909399;
  font-style: italic;
}

.verification-code-section {
  margin-top: 15px;
  width: 100%;

  .el-input {
    width: 100%;
  }
}

.auth-instruction {
  font-size: 14px;
  margin: 10px 0;
  text-align: center;

  strong {
    font-weight: bold;
  }

  /* 默认颜色（公众号） */
  &.wechat {
    color: #07C160;

    strong {
      color: #07C160;
    }
  }

  /* 抖音 */
  &.douyin {
    color: #000000;

    strong {
      color: #FE2C55;
    }
  }

  /* 快手 */
  &.kuaishou {
    color: #FF5600;

    strong {
      color: #FFB800;
    }
  }

  /* 视频号 */
  &.video-channel {
    color: #07C160;

    strong {
      color: #07C160;
    }
  }

  /* 小红书 */
  &.xiaohongshu {
    color: #FF2442;

    strong {
      color: #FF2442;
    }
  }

  /* B站 */
  &.bilibili {
    color: #FB7299;

    strong {
      color: #00A1D6;
    }
  }

  /* TikTok */
  &.tiktok {
    color: #000000;

    strong {
      color: #FE2C55;
    }
  }
}
</style>